import type { CpuDifficulty } from 'features/battle/ai-config';
import { badgeOf } from 'features/battle/rules';
import type { BattleInput, BattleState, PileIndex, PlayerId } from 'features/battle/types';
import { isBattleBadge, type BadgeStatKey } from 'features/catalog/badge-definitions';

export type TutorialSide = {
  hand: string[];
  /** Top of the deck first, so every draw is known ahead of time. */
  deck: string[];
};

export type TutorialMove =
  | { type: 'reveal'; badgeId: string; pile: PileIndex }
  | { type: 'play'; pile: PileIndex }
  | { type: 'resolve'; target: { kind: 'board_badge'; owner: PlayerId; pile: PileIndex } }
  | { type: 'draw' };

export type TutorialStep = {
  move: TutorialMove | readonly TutorialMove[];
  /** Your steps: what to do. CPU steps: what they did, shown once the move is made. */
  say: string;
  /** Shown first, behind a Next button. The board stays locked until the player reads past it. */
  intro?: string;
  /** Board stat to point at while the coach text names it. */
  stat?: BadgeStatKey;
};

export type TutorialTurn = { round: number; who: PlayerId; title: string; steps: TutorialStep[] };

export type BattleTutorial = {
  id: string;
  title: string;
  lesson: string;
  outro?: string;
  /** How to Play card eyebrow before this lesson is finished. */
  kicker: string;
  /** How to Play card eyebrow after this lesson is finished. */
  doneKicker: string;
  /** How to Play card action before this lesson is finished. */
  play: string;
  /** How to Play card action after this lesson is finished. */
  replay: string;
  /** Optional lessons do not glow as the required start. */
  optional?: boolean;
  /** This lesson stays locked until `requires` is finished. */
  requires?: string;
  /** How to Play card eyebrow while this lesson is locked. */
  lockedKicker?: string;
  /** How to Play card action while this lesson is locked. */
  lockedPlay?: string;
  you: TutorialSide;
  cpu: TutorialSide;
  /** Visible piles. The mini lesson uses Common + Rare only. */
  piles?: 2 | 4;
  /** CPU the free-play tail uses. Scripted turns ignore this. Defaults to Easy. */
  cpuDifficulty?: CpuDifficulty;
  /** Shown once the scripted turns finish if the battle is still going, while the player plays out the rest. */
  freePlay?: {
    title: string;
    say: string;
  };
  /** Every scripted action of the battle, in order. You open every round. */
  turns: TutorialTurn[];
};

export const BATTLE_MINI_TUTORIAL: BattleTutorial = {
  id: 'two-piles',
  title: 'Two Piles',
  lesson: 'Play a short guided battle to unlock Battle Badges and Battle Arena.',
  kicker: 'Guided battle',
  doneKicker: 'Completed',
  play: 'Start',
  replay: 'Play again',
  piles: 2,
  freePlay: {
    title: 'Your turn to win',
    say: 'You have the basics down. Complete the rest of the battle yourself and win!',
  },
  you: {
    hand: ['htci', 'hero', 'b4uv'],
    deck: ['srt2', 'perf', 't4lx', 'mx26', '7ays', 'eaze', 'jw0k', '9x0m', 'zqtm'],
  },
  cpu: {
    hand: ['9x0m', '3tgz', 'eaze', 'fyzv'],
    deck: ['ao3c', 'c4mp', 'b4uv', 'l1ys', '56jo', 'zprr', 'qpwb', 'tnlq'],
  },
  turns: [
    {
      round: 1,
      who: 0,
      title: 'Turn 1 · Your turn',
      steps: [
        {
          move: { type: 'reveal', badgeId: 'htci', pile: 0 },
          intro: 'Welcome to the Battle! Race your opponent to 10 Prestige. You will get 2 actions each turn.',
          say: 'Click the highlighted badge, then the unlocked pile to reveal it.',
        },
        {
          move: { type: 'play', pile: 0 },
          stat: 'spotlight',
          say: 'Click your revealed badge to activate it.',
        },
      ],
    },
    {
      round: 1,
      who: 1,
      title: 'Turn 1 · CPU’s turn',
      steps: [
        {
          move: [{ type: 'reveal', badgeId: '9x0m', pile: 0 }, { type: 'draw' }],
          intro: 'That’s your 2 actions. Click Next to watch the CPU’s turn.',
          stat: 'spotlight',
          say: 'They revealed a stats-only badge to take the Spotlight lead, then drew a badge. Without that lead, you cannot activate Spotlight effects.',
        },
      ],
    },
    {
      round: 2,
      who: 0,
      title: 'Turn 2 · Clear their board',
      steps: [
        {
          move: { type: 'reveal', badgeId: 'hero', pile: 1 },
          stat: 'engineering',
          say: 'Turn 2 opens the Rare pile. Reveal the highlighted badge to your empty pile.',
        },
        {
          move: [
            { type: 'play', pile: 1 },
            { type: 'resolve', target: { kind: 'board_badge', owner: 1, pile: 0 } },
          ],
          stat: 'engineering',
          say: 'Activate it, then click their badge to send it back to their deck.',
        },
      ],
    },
    {
      round: 2,
      who: 1,
      title: 'Turn 2 · CPU’s turn',
      steps: [
        {
          move: [
            { type: 'reveal', badgeId: '3tgz', pile: 1 },
            { type: 'play', pile: 1 },
          ],
          stat: 'fun',
          say: 'They revealed onto their Rare pile, and stole 1 Prestige by activating it.',
        },
      ],
    },
  ],
};

export const BATTLE_TUTORIAL: BattleTutorial = {
  id: 'first-battle',
  title: 'A Spotlight Comeback',
  lesson: 'A longer guided battle on the full four-pile board.',
  kicker: 'Optional',
  doneKicker: 'Completed',
  play: 'Play',
  replay: 'Play again',
  optional: true,
  requires: BATTLE_MINI_TUTORIAL.id,
  lockedKicker: 'Locked',
  lockedPlay: 'Finish Two Piles first',
  cpuDifficulty: 'hard',
  freePlay: {
    title: 'Your turn to win',
    say: 'Turn 4 unlocks the final pile. Make the comeback and win!',
  },
  you: {
    hand: ['htci', 'srt2', '0gfr'],
    deck: ['srt3', '9x0m', 'jw0k', 'xray', 'iced', 'uv79', '7ays', 'fyzv', 'b4uv'],
  },
  cpu: {
    hand: ['lvrp', '3tgz', 'ibdm', 'l1ys'],
    deck: ['bxbq', 'jt0d', 'krih', 'eaze', 'c4mp', 'iced', 'rac1', 'jw0k'],
  },
  turns: [
    {
      round: 1,
      who: 0,
      title: 'Turn 1 · Your turn',
      steps: [
        {
          move: { type: 'reveal', badgeId: 'htci', pile: 0 },
          say: 'A full battle has four piles. Reveal Buzz Builder onto the Common pile.',
        },
        {
          move: { type: 'play', pile: 0 },
          stat: 'spotlight',
          say: 'Activate it for 1 Prestige.',
        },
      ],
    },
    {
      round: 1,
      who: 1,
      title: 'Turn 1 · CPU’s turn',
      steps: [
        {
          move: [{ type: 'reveal', badgeId: 'lvrp', pile: 0 }, { type: 'draw' }],
          stat: 'spotlight',
          say: 'They revealed a stats-only badge to take the Spotlight lead, then drew a badge. Without that lead, you cannot activate Spotlight effects.',
        },
      ],
    },
    {
      round: 2,
      who: 0,
      title: 'Turn 2 · Take Spotlight back',
      steps: [
        {
          move: { type: 'reveal', badgeId: 'srt2', pile: 1 },
          stat: 'spotlight',
          say: 'Reveal SRT Knight onto the Rare pile to retake the Spotlight lead.',
        },
        {
          move: { type: 'play', pile: 1 },
          stat: 'spotlight',
          say: 'Activate it for 1 Prestige.',
        },
      ],
    },
    {
      round: 2,
      who: 1,
      title: 'Turn 2 · CPU’s turn',
      steps: [
        {
          move: [
            { type: 'reveal', badgeId: '3tgz', pile: 1 },
            { type: 'play', pile: 1 },
          ],
          stat: 'fun',
          say: 'They revealed onto their Rare pile, and stole 1 Prestige by activating it.',
        },
      ],
    },
    {
      round: 3,
      who: 0,
      title: 'Turn 3 · Legendary pile',
      steps: [
        {
          move: { type: 'reveal', badgeId: '0gfr', pile: 2 },
          say: 'Turn 3 opens the Legendary pile. Reveal Galactic Speaker.',
        },
        {
          move: { type: 'play', pile: 2 },
          stat: 'spotlight',
          say: 'Prestige is tied and you lead Spotlight. Activate it for 2 Prestige.',
        },
      ],
    },
    {
      round: 3,
      who: 1,
      title: 'Turn 3 · CPU’s turn',
      steps: [
        {
          move: [
            { type: 'reveal', badgeId: 'ibdm', pile: 2 },
            { type: 'play', pile: 2 },
          ],
          say: 'They revealed onto their Legendary pile, and gained 3 Prestige while behind to take a 4 to 3 lead.',
        },
      ],
    },
  ],
};

export const BATTLE_TUTORIALS: readonly BattleTutorial[] = [BATTLE_MINI_TUTORIAL, BATTLE_TUTORIAL];

export const battleTutorialById = (id: string): BattleTutorial | undefined =>
  BATTLE_TUTORIALS.find((tutorial) => tutorial.id === id);

export type TutorialStepRef = { turn: TutorialTurn; step: TutorialStep; key: string };

export type TutorialCursor = TutorialStepRef & {
  move: TutorialMove;
  index: number;
  number: number;
  /** The step played just before this one, if any. */
  previous: TutorialStepRef | null;
};

const isMoveArray = (move: TutorialStep['move']): move is readonly TutorialMove[] => Array.isArray(move);

export const tutorialStepMoves = (step: TutorialStep): readonly TutorialMove[] =>
  isMoveArray(step.move) ? step.move : [step.move];

export const tutorialStepCount = (tutorial: BattleTutorial): number =>
  tutorial.turns.reduce(
    (sum, turn) => sum + turn.steps.reduce((turnSum, step) => turnSum + tutorialStepMoves(step).length, 0),
    0
  );

const stepRef = (turn: TutorialTurn, index: number): TutorialStepRef => ({
  turn,
  step: turn.steps[index],
  key: `${turn.round}-${turn.who}-${index}`,
});

/** The last scripted step in the tutorial, if it is a CPU step. */
export const tutorialLastCpuStep = (tutorial: BattleTutorial): TutorialStepRef | null => {
  const turn = tutorial.turns[tutorial.turns.length - 1];
  if (!turn?.steps.length || turn.who !== 1) return null;
  return stepRef(turn, turn.steps.length - 1);
};

/** The last scripted CPU step, shown as a recap after a fully scripted battle ends. */
export const tutorialEndRecap = (tutorial: BattleTutorial, state: BattleState): TutorialStepRef | null => {
  if (tutorial.freePlay || state.phase !== 'game_over' || state.winner !== 0) return null;
  return tutorialLastCpuStep(tutorial);
};

/** The scripted step the battle is waiting on, or null once it is over or has left the script. */
export const tutorialCursor = (tutorial: BattleTutorial, state: BattleState): TutorialCursor | null => {
  if (state.phase === 'game_over') return null;
  let number = 0;
  let previous: TutorialStepRef | null = null;
  for (const turn of tutorial.turns) {
    const turnMoves = turn.steps.reduce((sum, step) => sum + tutorialStepMoves(step).length, 0);
    if (turn.round === state.round && turn.who === state.activePlayer) {
      const actionsTaken = state.players[turn.who].actionsTaken;
      let actionsSeen = 0;
      let moveNumber = 0;
      for (let index = 0; index < turn.steps.length; index += 1) {
        const moves = tutorialStepMoves(turn.steps[index]);
        for (const move of moves) {
          moveNumber += 1;
          const matches =
            move.type === 'resolve'
              ? Boolean(state.pending) && actionsSeen === actionsTaken
              : !state.pending && actionsSeen === actionsTaken;
          if (matches) {
            return {
              ...stepRef(turn, index),
              move,
              index,
              number: number + moveNumber,
              previous: index > 0 ? stepRef(turn, index - 1) : previous,
            };
          }
          if (move.type !== 'resolve') actionsSeen += 1;
        }
      }
      return null;
    }
    number += turnMoves;
    if (turn.steps.length) previous = stepRef(turn, turn.steps.length - 1);
  }
  return null;
};

export const tutorialInput = (state: BattleState, move: TutorialMove): BattleInput => {
  if (move.type !== 'reveal') return move;
  const cardId = state.players[state.activePlayer].hand.find((id) => badgeOf(state, id) === move.badgeId);
  if (!cardId) throw new Error(`Tutorial reveal: ${move.badgeId} is not in hand.`);
  return { type: 'reveal', cardId, pile: move.pile };
};

const sameInput = (state: BattleState, input: BattleInput, move: TutorialMove): boolean => {
  if (input.type === 'draw' || move.type === 'draw') return input.type === move.type;
  if (input.type === 'play') return move.type === 'play' && input.pile === move.pile;
  if (input.type === 'reveal') {
    return move.type === 'reveal' && input.pile === move.pile && badgeOf(state, input.cardId) === move.badgeId;
  }
  if (input.type === 'resolve' && move.type === 'resolve') {
    return (
      input.target.kind === 'board_badge' &&
      move.target.kind === 'board_badge' &&
      input.target.owner === move.target.owner &&
      input.target.pile === move.target.pile
    );
  }
  return false;
};

/** Only the scripted move goes through. Off the script nothing is held back, so the battle can't get stuck. */
export const tutorialAllows = (tutorial: BattleTutorial, state: BattleState, input: BattleInput): boolean => {
  if (input.type === 'pass' || input.type === 'undo') return true;
  const cursor = tutorialCursor(tutorial, state);
  return !cursor || sameInput(state, input, cursor.move);
};

export const tutorialBadgeIds = (side: TutorialSide): string[] => [...side.hand, ...side.deck];

/** The board stat this coach text is talking about, if the step marked one and the text names it. */
export const tutorialFocusStat = (step: TutorialStep, text: string): BadgeStatKey | null => {
  if (!step.stat) return null;
  return text.toLowerCase().includes(step.stat) ? step.stat : null;
};

export const tutorialUsesOnlyBattleBadges = (tutorial: BattleTutorial): boolean =>
  [...tutorialBadgeIds(tutorial.you), ...tutorialBadgeIds(tutorial.cpu)].every((id) => isBattleBadge(id));
